// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledOverlay from "@ces/App/Components/Common/StyledOverlay";
import XngUtilities from "@ces/App/Components/Common/XngUtilities";
import XngZone from "@ces/App/Components/Common/XngZone";
import xHeaderUpdate from "@ces/App/Handlers/XNG/Header";
import requestedPickupTimes from "@ces/App/Handlers/XNG/RequestedPickupTimes";
import useSize from "@ces/App/Hooks/useSize";
import { CustomAppState } from "@ces/App/Store/Reducers";
import { getLogoImage, xIsAuthenticated } from "@ces/App/Store/Selectors/GeneralSelectors";
import { checkoutPages } from "@ces/XDL/constants";
import { EagerLoadingLogoProvider } from "@insite/client-framework/Common/EagerLoadingLogo";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import Zone from "@insite/client-framework/Components/Zone";
import setDrawerIsOpen from "@insite/client-framework/Store/Components/AddressDrawer/Handlers/SetDrawerIsOpen";
import setNavDrawerIsOpen from "@insite/client-framework/Store/Components/AddressDrawer/Handlers/SetNavDrawerIsOpen";
import { getIsPunchOutSession, getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import dismissRfqUpdates from "@insite/client-framework/Store/Context/Handlers/DismissRfqUpdates";
import { getCurrentPage, getLocation, getReturnUrl } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import loadCurrentShipTo from "@insite/client-framework/Store/Data/ShipTos/Handlers/LoadCurrentShipTo";
import { getCurrentShipToState } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import SkipNav, { SkipNavStyles } from "@insite/content-library/Components/SkipNav";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Hidden from "@insite/mobius/Hidden";
import Link from "@insite/mobius/Link";
import Modal from "@insite/mobius/Modal";
import Page, { PageProps as MobiusPageProps } from "@insite/mobius/Page";
import { HasToasterContext, withToaster } from "@insite/mobius/Toast/ToasterContext";
import Typography from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useEffect, useRef } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

// eslint-disable-next-line spire/fenced-imports
import { hideAll } from "tippy.js";

const mapStateToProps = (state: CustomAppState) => {
    const returnUrl = getReturnUrl(state);
    const shippingPageUrl = getPageLinkByPageType(state, "CheckoutShippingPage")?.url;
    const checkoutReviewAndSubmitPageUrl = getPageLinkByPageType(state, "CheckoutReviewAndSubmitPage")?.url;
    const referredFromCheckout = returnUrl === shippingPageUrl || returnUrl === checkoutReviewAndSubmitPageUrl;
    const isAuthenticated = xIsAuthenticated(state);
    return {
        siteName: state.context.website.name,
        session: getSession(state),
        myAccountPageUrl: getPageLinkByPageType(state, "MyAccountPage")?.url,
        // XNG-Changes
        isTabletScreen: state.xCommon.isTabletScreen,
        currentLocation: getLocation(state),
        isSticky: state.xHeader.isSticky,
        isCheckoutPage:
            checkoutPages.includes(getCurrentPage(state).type) ||
            getCurrentPage(state).type === "RfqCustomRequestQuotePage" ||
            (referredFromCheckout && getCurrentPage(state).type === "SignInPage"),
        logoImage: getLogoImage(state),
        navDrawerIsOpen: state.components.addressDrawer.navDrawerIsOpen,
        isAuthenticated,
        currentShipToState: getCurrentShipToState(state),
        isPunchOutSession: getIsPunchOutSession(state),
        isDrawerOpen: state.components.addressDrawer.isOpen,
    };
};

const mapDispatchToProps = {
    dismissRfqUpdates,
    loadCurrentShipTo,
    // XNG-Changes
    xHeaderUpdate,
    setNavDrawerIsOpen,
    requestedPickupTimes,
    setDrawerIsOpen,
};

type Props = PageProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasToasterContext &
    HasHistory;

export interface HeaderStyles {
    page?: MobiusPageProps;
    container?: InjectableCss;
    skipToContent?: SkipNavStyles;
    websiteName?: InjectableCss;
    headerWrapper?: InjectableCss;
    // XNG
    printLogo?: InjectableCss;
    MainHeaderContainer?: InjectableCss;
    upperLineHeader?: InjectableCss;
    bottomLineHeader?: InjectableCss;
    rightSide?: InjectableCss;
    headerCustomButtons?: InjectableCss;
}

export const headerStyles: HeaderStyles = {
    page: {
        fullWidth: [true, true, false, false, false],
    },
    container: {
        css: css`
            background-color: ${getColor("secondary.main")};
            display: flex;
            > div:first-child {
                flex-grow: 1;
            }
            > div:last-child {
                max-width: 150px;
                display: flex;
                align-items: center;
                justify-content: center;
                height: auto;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            null,
                            css`
                                max-width: 100px;
                            `,
                        ],
                        "max",
                    )}
            }
        `,
    },
    websiteName: {
        css: css`
            @media print {
                display: block;
            }
            font-size: 12px;
            padding-left: 15px;
            display: none;
        `,
    },
};

const styles = headerStyles;

const Header = ({
    id,
    siteName,
    session,
    toaster,
    history,
    myAccountPageUrl,
    dismissRfqUpdates,
    currentLocation,
    isSticky,
    isCheckoutPage,
    xHeaderUpdate,
    logoImage,
    navDrawerIsOpen,
    isAuthenticated,
    currentShipToState,
    loadCurrentShipTo,
    setNavDrawerIsOpen,
    requestedPickupTimes,
    // XNG-Changes
    isPunchOutSession,
    isDrawerOpen,
    setDrawerIsOpen,
}: Props) => {
    const headerRef = useRef<HTMLDivElement>(null);
    const headerHeight = useSize(headerRef)?.height;
    const viewMyDashboardClickHandler = () => {
        if (myAccountPageUrl) {
            history.push(myAccountPageUrl);
        }
    };

    useEffect(() => {
        if (session.hasRfqUpdates && myAccountPageUrl) {
            toaster.addToast({
                body: (
                    <>
                        <Typography>{translate("You have new quote information.")}</Typography>
                        <Link onClick={viewMyDashboardClickHandler} aria-label="View My Dashboard">
                            {translate("View My Dashboard")}
                        </Link>
                    </>
                ),
                messageType: "info",
                timeoutLength: 1000 * 60 * 60 * 24 * 365,
                onClose: () => {
                    dismissRfqUpdates();
                },
            });
        }
    }, [myAccountPageUrl]);

    // Hide main navigation
    useEffect(() => {
        setTimeout(() => hideAll());
        if (navDrawerIsOpen) {
            setNavDrawerIsOpen({ navDrawerIsOpen: false });
            setTimeout(() => setNavDrawerIsOpen({ navDrawerIsOpen: undefined }), 300);
        }
    }, [isSticky, currentLocation]);

    // XNG-Changes
    useEffect(() => {
        if (headerHeight) {
            // include border-bottom
            xHeaderUpdate({ key: "headerHeight", value: `${headerHeight + 1}px` });
        }
    }, [headerHeight]);

    // XNG-Change
    useEffect(() => {
        if (isAuthenticated && !currentShipToState.value && !currentShipToState.isLoading) {
            loadCurrentShipTo();
        }
    }, []);

    useEffect(() => {
        if (isAuthenticated) {
            requestedPickupTimes({
                warehouseOpenTime: session.properties?.warehouseOpenTime,
                warehouseCloseTime: session.properties?.warehouseCloseTime,
            });
        }
    }, [session.properties?.warehouseOpenTime, session.properties?.warehouseCloseTime]);

    // XNG-Changes
    const handleCloseDrawer = () => {
        setDrawerIsOpen({ isOpen: false });
    };

    const afterHeader = React.createRef<HTMLSpanElement>();
    return (
        <>
            {/* XNG-Changes: add logo for print view */}
            {logoImage && (
                <StyledWrapper {...styles.printLogo}>
                    <img alt={translate("Home")} src={logoImage} {...styles.printLogo} />
                </StyledWrapper>
            )}
            <StyledWrapper
                ref={headerRef}
                {...styles.headerWrapper}
                className={isCheckoutPage ? "checkout-header" : ""}
            >
                <Page as="header" isHeader={true} {...styles.page} {...{ isCheckoutPage }}>
                    <SkipNav
                        extendedStyles={styles.skipToContent}
                        text={translate("Skip to main content")}
                        destination={afterHeader}
                    />
                    <EagerLoadingLogoProvider value={true}>
                        {!isCheckoutPage && (
                            <XngZone contentId={id} zoneName="SecondaryNavigation" hint="Links & Rich content" />
                        )}
                        <StyledWrapper {...styles.container} {...{ isCheckoutPage }}>
                            {!isCheckoutPage && (
                                <Hidden above="md">
                                    <XngZone contentId={id} zoneName="MainNavigation" hint="MainNavigation" />
                                </Hidden>
                            )}
                            <XngZone contentId={id} zoneName="logo" />
                            {isCheckoutPage && (
                                <StyledWrapper className="header-message" {...styles.rightSide}>
                                    <XngZone hint="Spot" contentId={id} zoneName="Spot" />
                                </StyledWrapper>
                            )}
                            <StyledWrapper {...styles.MainHeaderContainer} {...{ isCheckoutPage }}>
                                <StyledWrapper {...styles.upperLineHeader}>
                                    <XngZone contentId={id} zoneName="Search" hint="Search" />
                                    <XngZone contentId={id} zoneName="Login_Cart" hint="Login & Cart" />
                                </StyledWrapper>
                                <Hidden below="lg">
                                    <StyledWrapper {...styles.bottomLineHeader}>
                                        <XngZone contentId={id} zoneName="MainNavigation" hint="MainNavigation" />
                                        {/* XNG-Changes */}
                                        <StyledWrapper {...styles.headerCustomButtons}>
                                            <XngZone
                                                contentId={id}
                                                zoneName="HeaderCustomButtons"
                                                hint="HeaderCustomButtons"
                                            />
                                        </StyledWrapper>
                                    </StyledWrapper>
                                </Hidden>
                            </StyledWrapper>
                        </StyledWrapper>
                        <Zone contentId={id} zoneName="Breadcrumb" />
                    </EagerLoadingLogoProvider>
                    <span ref={afterHeader} tabIndex={-1} />
                </Page>
                {/* <XNG-Changes> */}
                <XngUtilities />
                <StyledOverlay />
                {/* XNG-Changes: modal */}
                <Modal
                    data-test-selector="addressDrawer"
                    isOpen={isDrawerOpen}
                    headline={isPunchOutSession ? "" : translate("Change Fulfillment Method")}
                    handleClose={handleCloseDrawer}
                >
                    {createWidgetElement("Header/AddressDrawerContainer", {
                        fields: {},
                    })}
                </Modal>
                {/* </XNG-Changes> */}
            </StyledWrapper>
        </>
    );
};

const pageModule: PageModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withToaster(withHistory(Header))),
    definition: {
        hasEditableTitle: false,
        hasEditableUrlSegment: false,
        pageType: "System",
    },
};

export const HeaderContext = "HeaderContext";

export default pageModule;
